<template>
  <layout-header />
  <layout-sidebar />
  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex align-items-center justify-content-between mb-3">
        <breadcrumb
          title="Optional Monthly Adjustments"
          text="Payroll"
          text1="Optional Monthly Adjustments"
        />
        <button
          v-can="'Payroll.Edit'"
          class="btn btn-primary"
          type="button"
          @click="startCreate"
        >
          <i class="ti ti-circle-plus me-1"></i>Add optional adjustment
        </button>
      </div>

      <div class="d-flex flex-wrap gap-2 mb-3">
        <router-link to="/payroll/payroll" class="btn btn-white border">
          Recurring payments
        </router-link>
        <router-link to="/payroll/payroll-deduction" class="btn btn-white border">
          Recurring deductions
        </router-link>
        <router-link to="/payroll/period-adjustments" class="btn btn-primary">
          Optional Monthly Adjustments
        </router-link>
      </div>

      <div v-if="showForm" class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">
            {{ editingId ? "Edit optional monthly adjustment" : "New optional monthly adjustment" }}
          </h5>
          <p class="text-muted small mb-0">
            This screen is optional. Use “Once” for a one-off bonus, responsibility
            allowance, or similar period amount. Mid-month advances are recovered
            automatically on Final payroll; multi-month repayment belongs under
            Employee Loans.
          </p>
        </div>
        <form class="card-body" @submit.prevent="save">
          <div class="row g-3">
            <div class="col-md-3">
              <label class="form-label">Direction</label>
              <select v-model="form.direction" class="form-select">
                <option value="addition">Addition</option>
                <option value="deduction">Deduction</option>
              </select>
            </div>
            <div class="col-md-5">
              <label class="form-label">Name</label>
              <input
                v-model.trim="form.name"
                class="form-control"
                required
                placeholder="e.g. Responsibility"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label">Category</label>
              <select
                v-model="categoryChoice"
                class="form-select"
                @change="onCategoryChoiceChange"
              >
                <option
                  v-for="opt in categoryOptions"
                  :key="opt.value || 'blank'"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
              <input
                v-if="categoryChoice === '__custom__'"
                v-model.trim="form.category"
                class="form-control mt-2"
                placeholder="Type a short category key"
              />
              <div class="form-text">
                Pick from the list when you can. Category is an internal label;
                the Name is what appears on the payslip. Leave blank if unsure.
              </div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Recurrence</label>
              <select
                v-model="form.recurrence"
                class="form-select"
              >
                <option value="once">Once</option>
                <option value="recurring">Recurring</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label">Frequency</label>
              <select v-model="form.frequency" class="form-select">
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="daily">Daily</option>
              </select>
            </div>
            <div v-if="form.recurrence === 'once'" class="col-md-3">
              <label class="form-label">Payroll month</label>
              <input
                v-model="form.period"
                class="form-control"
                type="month"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label">Run type</label>
              <select v-model="form.run_type" class="form-select">
                <option value="regular">Regular</option>
                <option value="final">Final</option>
                <option value="any">Any</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label">Calculation</label>
              <select v-model="form.calculation_mode" class="form-select">
                <option value="fixed">Fixed amount</option>
                <option value="unit">Rate × units</option>
              </select>
            </div>
            <div v-if="form.calculation_mode === 'fixed'" class="col-md-3">
              <label class="form-label">Amount</label>
              <input
                v-model.number="form.amount"
                class="form-control"
                type="number"
                min="0"
                step="0.01"
                required
              />
            </div>
            <template v-else>
              <div class="col-md-3">
                <label class="form-label">Rate</label>
                <input
                  v-model.number="form.rate"
                  class="form-control"
                  type="number"
                  min="0"
                  step="0.0001"
                  required
                />
              </div>
              <div class="col-md-3">
                <label class="form-label">Units</label>
                <input
                  v-model.number="form.units"
                  class="form-control"
                  type="number"
                  min="0"
                  step="0.0001"
                  required
                />
              </div>
            </template>
            <div v-if="form.recurrence === 'recurring'" class="col-md-3">
              <label class="form-label">Effective from</label>
              <input
                v-model="form.effective_from"
                class="form-control"
                type="date"
              />
            </div>
            <div v-if="form.recurrence === 'recurring'" class="col-md-3">
              <label class="form-label">Effective to</label>
              <input
                v-model="form.effective_to"
                class="form-control"
                type="date"
              />
            </div>
            <div class="col-12">
              <EmployeeSelectDropdown
                v-model="form.employee_ids"
                :employees="employeeDropdownEmployees"
                mode="multi"
                label="Employees"
                :help="
                  editingId
                    ? 'Employee cannot be changed while editing. Delete and recreate to move it.'
                    : 'Search and select one or more employees.'
                "
                placeholder="Select employees"
                :disabled="!!editingId"
                :invalid="triedSave && !form.employee_ids.length"
                :error="triedSave && !form.employee_ids.length ? 'Select at least one employee.' : ''"
              />
            </div>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="btn btn-white border" type="button" @click="cancelForm">
              Cancel
            </button>
            <button class="btn btn-primary" type="submit" :disabled="saving">
              {{
                saving
                  ? "Saving…"
                  : editingId
                    ? "Update adjustment"
                    : "Save adjustment"
              }}
            </button>
          </div>
        </form>
      </div>

      <div class="card">
        <div class="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <h5 class="mb-0">Resolved payroll inputs</h5>
            <small class="text-muted">Review these entries before generating payroll.</small>
          </div>
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <button
              v-can="'Payroll.Edit'"
              class="btn btn-outline-danger"
              type="button"
              :disabled="!selectedIds.length || bulkDeleting"
              @click="askDeleteSelected"
            >
              <i class="ti ti-trash me-1"></i>
              {{
                bulkDeleting
                  ? "Deleting…"
                  : `Delete selected${selectedIds.length ? ` (${selectedIds.length})` : ""}`
              }}
            </button>
            <input
              v-model.trim="listSearch"
              class="form-control"
              type="search"
              placeholder="Search employee…"
              style="min-width: 200px"
            />
            <input v-model="filterPeriod" class="form-control" type="month" />
            <button class="btn btn-white border" type="button" @click="loadEntries">
              Refresh
            </button>
          </div>
        </div>
        <div
          v-if="hasProvisionalEntries"
          class="alert alert-warning mb-0 rounded-0 border-0 border-bottom"
        >
          <strong>Provisional / Pay100 rows for this month will be included</strong>
          when you generate September (or the filtered month) payroll.
          Keep only amounts that should still be paid; delete rows that should not.
        </div>
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th style="width: 42px">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :checked="allPageSelected"
                    :indeterminate.prop="somePageSelected && !allPageSelected"
                    @change="toggleSelectAllPage"
                  />
                </th>
                <th>Employee</th>
                <th>Entry</th>
                <th>Period</th>
                <th>Source</th>
                <th class="text-end">Amount</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="text-center py-4">Loading…</td>
              </tr>
              <tr v-else-if="!entries.length">
                <td colspan="7" class="text-center text-muted py-4">
                  No period adjustments found.
                </td>
              </tr>
              <tr v-for="entry in entries" :key="entry.id">
                <td>
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :checked="selectedIds.includes(entry.id)"
                    @change="toggleSelected(entry.id)"
                  />
                </td>
                <td>
                  <div class="fw-semibold">{{ employeeName(entry.employee) }}</div>
                  <small class="text-muted">{{ entry.employee?.employee_id }}</small>
                </td>
                <td>
                  <span
                    class="badge me-1"
                    :class="entry.direction === 'addition' ? 'bg-success' : 'bg-danger'"
                  >
                    {{ entry.direction }}
                  </span>
                  {{ entry.name }}
                  <span v-if="entry.meta?.provisional" class="badge bg-warning ms-1">
                    Provisional
                  </span>
                </td>
                <td>{{ entry.period_label || entry.period_code || "Recurring" }}</td>
                <td>{{ entry.source_type || "manual" }}</td>
                <td class="text-end fw-semibold">{{ money(entry.amount) }}</td>
                <td class="text-end text-nowrap">
                  <div class="action-icon d-inline-flex">
                    <a
                      v-can="'Payroll.Edit'"
                      href="javascript:void(0);"
                      class="me-2"
                      title="Edit"
                      @click.prevent="startEdit(entry)"
                    >
                      <i class="ti ti-edit"></i>
                    </a>
                    <a
                      v-can="'Payroll.Edit'"
                      href="javascript:void(0);"
                      title="Delete"
                      @click.prevent="askDeleteEntry(entry)"
                    >
                      <i class="ti ti-trash"></i>
                    </a>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          class="card-footer d-flex flex-wrap align-items-center justify-content-between gap-2"
        >
          <div class="text-muted small">
            <template v-if="pagination.total">
              Showing {{ pagination.from || 0 }}–{{ pagination.to || 0 }} of
              {{ pagination.total }}
            </template>
            <template v-else>No results</template>
          </div>
          <div class="d-flex flex-wrap align-items-center gap-2">
            <select
              v-model.number="perPage"
              class="form-select form-select-sm"
              style="width: auto"
            >
              <option :value="10">10 / page</option>
              <option :value="25">25 / page</option>
              <option :value="50">50 / page</option>
              <option :value="100">100 / page</option>
            </select>
            <div class="btn-group">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :disabled="loading || pagination.current_page <= 1"
                @click="goToPage(pagination.current_page - 1)"
              >
                Prev
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" disabled>
                Page {{ pagination.current_page }} / {{ totalPages }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :disabled="loading || pagination.current_page >= totalPages"
                @click="goToPage(pagination.current_page + 1)"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete confirm (system modal) -->
  <div
    class="modal fade"
    id="delete_period_adjustment_modal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content p-3 text-center">
        <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3 mx-auto">
          <i class="ti ti-trash-x fs-36"></i>
        </span>
        <h4 class="mb-1">Confirm Delete</h4>
        <p class="mb-0">
          <template v-if="deleteMode === 'bulk'">
            You want to delete
            <strong>{{ pendingDeleteIds.length }}</strong>
            selected adjustment{{ pendingDeleteIds.length === 1 ? "" : "s" }}.
            This cannot be undone.
          </template>
          <template v-else>
            You want to delete
            <strong>{{ pendingDeleteLabel }}</strong>.
            This cannot be undone.
          </template>
        </p>
        <div class="d-flex justify-content-center mt-3">
          <button
            class="btn btn-light me-3"
            type="button"
            data-bs-dismiss="modal"
            :disabled="deleting"
          >
            Cancel
          </button>
          <button
            class="btn btn-danger"
            type="button"
            :disabled="deleting"
            @click="confirmDelete"
          >
            {{ deleting ? "Deleting…" : "Yes, Delete" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { Modal } from "bootstrap";
import employeeService from "@/services/hrm/employeeService";
import adjustmentService from "@/services/payrollComponentEntry.service";
import EmployeeSelectDropdown from "@/components/common/EmployeeSelectDropdown.vue";
import { toast } from "@/utils/toast";

const route = useRoute();
const now = new Date();
const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
const employees = ref([]);
const entries = ref([]);
const loading = ref(false);
const saving = ref(false);
const bulkDeleting = ref(false);
const deleting = ref(false);
const showForm = ref(false);
const triedSave = ref(false);
const editingId = ref(null);
const selectedIds = ref([]);
const deleteMode = ref("single"); // single | bulk
const pendingDeleteIds = ref([]);
const pendingDeleteLabel = ref("");
const filterPeriod = ref(
  typeof route.query.period === "string" && /^\d{4}-\d{2}$/.test(route.query.period)
    ? route.query.period
    : currentMonth,
);
const listSearch = ref("");
const currentPage = ref(1);
const perPage = ref(25);
const pagination = ref({
  current_page: 1,
  last_page: 1,
  per_page: 25,
  total: 0,
  from: 0,
  to: 0,
});
const categoryChoice = ref("responsibility_allowance");
let searchTimer = null;

const categoryOptions = [
  { value: "responsibility_allowance", label: "Responsibility allowance" },
  { value: "bonus", label: "Bonus" },
  { value: "acting_allowance", label: "Acting allowance" },
  { value: "other_allowance", label: "Other taxable allowance" },
  { value: "recovery", label: "Recovery / clawback" },
  { value: "", label: "No category (optional)" },
  { value: "__custom__", label: "Custom…" },
];

const employeeDropdownEmployees = computed(() =>
  (employees.value || []).map((e) => ({
    id: Number(e.id),
    name: employeeName(e),
    employee_id: e.employee_id,
    avatar_url: e.avatar_url || "",
    department: e.department?.name || e.department || "",
    designation: e.designation?.name || e.designation || "",
  })),
);

const pageIds = computed(() => entries.value.map((entry) => entry.id));

const allPageSelected = computed(
  () =>
    pageIds.value.length > 0 &&
    pageIds.value.every((id) => selectedIds.value.includes(id)),
);

const somePageSelected = computed(() =>
  pageIds.value.some((id) => selectedIds.value.includes(id)),
);

const totalPages = computed(() => Math.max(1, Number(pagination.value.last_page || 1)));

const hasProvisionalEntries = computed(() =>
  entries.value.some(
    (entry) =>
      entry?.meta?.provisional ||
      String(entry?.source_type || "").includes("provisional") ||
      String(entry?.source_type || "") === "pay100_provisional",
  ),
);

const newForm = () => ({
  direction: "addition",
  name: "Responsibility",
  category: "responsibility_allowance",
  calculation_mode: "fixed",
  amount: 0,
  rate: null,
  units: null,
  recurrence: "once",
  frequency: "monthly",
  period: filterPeriod.value || currentMonth,
  run_type: "regular",
  employee_ids: [],
  effective_from: "",
  effective_to: "",
  source_type: "manual",
  meta: {},
});
const form = reactive(newForm());

function resolveCategoryChoice(category) {
  const value = String(category || "");
  if (categoryOptions.some((opt) => opt.value === value && opt.value !== "__custom__")) {
    return value;
  }
  return value ? "__custom__" : "";
}

function periodFromEntry(entry) {
  const code = String(entry?.period_code || entry?.period_label || "");
  const match = code.match(/^(\d{4}-\d{2})/);
  if (match) return match[1];
  if (entry?.period_start) return String(entry.period_start).slice(0, 7);
  return filterPeriod.value || currentMonth;
}

function onCategoryChoiceChange() {
  if (categoryChoice.value === "__custom__") {
    if (
      categoryOptions.some(
        (opt) => opt.value === form.category && opt.value !== "__custom__",
      )
    ) {
      form.category = "";
    }
    return;
  }
  form.category = categoryChoice.value;
  if (categoryChoice.value === "responsibility_allowance" && !form.name) {
    form.name = "Responsibility";
  }
  if (categoryChoice.value === "bonus" && !form.name) {
    form.name = "Bonus";
  }
}

function employeeName(employee = {}) {
  return (
    employee.full_name ||
    [employee.first_name, employee.middle_name, employee.last_name]
      .filter(Boolean)
      .join(" ") ||
    "Employee"
  );
}

function money(value) {
  return `KES ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function resetFormState() {
  Object.assign(form, newForm());
  categoryChoice.value = "responsibility_allowance";
  editingId.value = null;
  triedSave.value = false;
}

function startCreate() {
  resetFormState();
  showForm.value = true;
}

function cancelForm() {
  showForm.value = false;
  resetFormState();
}

function startEdit(entry) {
  editingId.value = entry.id;
  Object.assign(form, {
    direction: entry.direction || "addition",
    name: entry.name || "",
    category: entry.category || "",
    calculation_mode: entry.calculation_mode || "fixed",
    amount: Number(entry.amount || 0),
    rate: entry.rate == null ? null : Number(entry.rate),
    units: entry.units == null ? null : Number(entry.units),
    recurrence: entry.recurrence || "once",
    frequency: entry.frequency || "monthly",
    period: periodFromEntry(entry),
    run_type: entry.run_type || "regular",
    employee_ids: entry.employee_id ? [Number(entry.employee_id)] : [],
    effective_from: entry.effective_from
      ? String(entry.effective_from).slice(0, 10)
      : "",
    effective_to: entry.effective_to ? String(entry.effective_to).slice(0, 10) : "",
    source_type: entry.source_type || "manual",
    meta: entry.meta || {},
  });
  categoryChoice.value = resolveCategoryChoice(entry.category);
  triedSave.value = false;
  showForm.value = true;
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function toggleSelected(id) {
  if (selectedIds.value.includes(id)) {
    selectedIds.value = selectedIds.value.filter((item) => item !== id);
  } else {
    selectedIds.value = [...selectedIds.value, id];
  }
}

function toggleSelectAllPage(event) {
  const checked = !!event?.target?.checked;
  if (checked) {
    selectedIds.value = Array.from(
      new Set([...selectedIds.value, ...pageIds.value]),
    );
    return;
  }
  const pageSet = new Set(pageIds.value);
  selectedIds.value = selectedIds.value.filter((id) => !pageSet.has(id));
}

function goToPage(page) {
  const next = Math.min(Math.max(1, Number(page) || 1), totalPages.value);
  if (next === currentPage.value) return;
  currentPage.value = next;
  loadEntries();
}

async function loadEmployees() {
  const { data } = await employeeService.getAll({ status: "Active", per_page: 500 });
  employees.value = data?.data || data?.rows?.data || data || [];
}

async function loadEntries() {
  loading.value = true;
  try {
    const { data } = await adjustmentService.list({
      period_code: filterPeriod.value,
      q: listSearch.value || undefined,
      page: currentPage.value,
      per_page: perPage.value,
    });
    entries.value = data?.data || [];
    pagination.value = {
      current_page: Number(data?.current_page || 1),
      last_page: Number(data?.last_page || 1),
      per_page: Number(data?.per_page || perPage.value),
      total: Number(data?.total || 0),
      from: Number(data?.from || 0),
      to: Number(data?.to || 0),
    };
    currentPage.value = pagination.value.current_page;
    const visible = new Set(entries.value.map((entry) => entry.id));
    // Keep selections from other pages; drop ids no longer returned only when searching empty set overall
    if (!pagination.value.total) {
      selectedIds.value = [];
    } else {
      // leave cross-page selection intact
      void visible;
    }
  } catch (error) {
    toast(error?.response?.data?.message || "Failed to load period adjustments.", "error");
  } finally {
    loading.value = false;
  }
}

async function save() {
  triedSave.value = true;
  if (!form.employee_ids.length) {
    toast("Select at least one employee.", "error");
    return;
  }
  saving.value = true;
  try {
    const payload = {
      direction: form.direction,
      name: form.name,
      category: form.category || null,
      calculation_mode: form.calculation_mode,
      amount: Number(form.amount || 0),
      rate: form.calculation_mode === "unit" ? Number(form.rate || 0) : null,
      units: form.calculation_mode === "unit" ? Number(form.units || 0) : null,
      recurrence: form.recurrence,
      frequency: form.frequency,
      period: form.recurrence === "once" ? form.period || null : null,
      run_type: form.run_type,
      employee_ids: form.employee_ids.map(Number),
      effective_from:
        form.recurrence === "recurring" ? form.effective_from || null : null,
      effective_to:
        form.recurrence === "recurring" ? form.effective_to || null : null,
      source_type: form.source_type || "manual",
      meta: form.meta || {},
    };

    if (editingId.value) {
      const meta = { ...(form.meta || {}) };
      delete meta.provisional;
      await adjustmentService.update(editingId.value, {
        ...payload,
        source_type: "manual",
        meta,
        manual_override: true,
      });
      toast("Payroll adjustment updated.", "success");
    } else {
      await adjustmentService.createBulk({
        ...payload,
        idempotency_key: `manual:${Date.now()}`,
      });
      toast("Payroll adjustment saved.", "success");
    }

    filterPeriod.value = payload.period || filterPeriod.value;
    currentPage.value = 1;
    cancelForm();
    await loadEntries();
  } catch (error) {
    toast(
      error?.response?.data?.message ||
        (editingId.value
          ? "Failed to update payroll adjustment."
          : "Failed to save payroll adjustment."),
      "error",
    );
  } finally {
    saving.value = false;
  }
}

function openDeleteModal() {
  const el = document.getElementById("delete_period_adjustment_modal");
  if (!el) return;
  Modal.getOrCreateInstance(el).show();
}

function closeDeleteModal() {
  const el = document.getElementById("delete_period_adjustment_modal");
  if (!el) return;
  const modal = Modal.getInstance(el) || Modal.getOrCreateInstance(el);
  modal.hide();
}

function askDeleteEntry(entry) {
  deleteMode.value = "single";
  pendingDeleteIds.value = [entry.id];
  pendingDeleteLabel.value = `${entry.name} for ${employeeName(entry.employee)}`;
  openDeleteModal();
}

function askDeleteSelected() {
  if (!selectedIds.value.length) return;
  deleteMode.value = "bulk";
  pendingDeleteIds.value = [...selectedIds.value];
  pendingDeleteLabel.value = "";
  openDeleteModal();
}

async function confirmDelete() {
  if (!pendingDeleteIds.value.length) return;
  const ids = [...pendingDeleteIds.value];
  const isBulk = deleteMode.value === "bulk" || ids.length > 1;

  deleting.value = true;
  if (isBulk) bulkDeleting.value = true;

  try {
    if (isBulk) {
      const { data } = await adjustmentService.removeBulk(ids);
      if (editingId.value && ids.includes(editingId.value)) {
        cancelForm();
      }
      selectedIds.value = selectedIds.value.filter((id) => !ids.includes(id));
      toast(
        `Deleted ${data?.deleted ?? ids.length} payroll adjustment${
          (data?.deleted ?? ids.length) === 1 ? "" : "s"
        }.`,
        "success",
      );
    } else {
      await adjustmentService.remove(ids[0]);
      selectedIds.value = selectedIds.value.filter((id) => id !== ids[0]);
      if (editingId.value === ids[0]) cancelForm();
      toast("Payroll adjustment deleted.", "success");
    }

    pendingDeleteIds.value = [];
    closeDeleteModal();
    await loadEntries();
  } catch (error) {
    toast(
      error?.response?.data?.message ||
        (isBulk
          ? "Failed to delete selected payroll adjustments."
          : "Failed to delete payroll adjustment."),
      "error",
    );
  } finally {
    deleting.value = false;
    bulkDeleting.value = false;
  }
}

watch(filterPeriod, () => {
  currentPage.value = 1;
  selectedIds.value = [];
  loadEntries();
});

watch(perPage, () => {
  currentPage.value = 1;
  loadEntries();
});

watch(listSearch, () => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    currentPage.value = 1;
    loadEntries();
  }, 300);
});

onMounted(async () => {
  await Promise.all([loadEmployees(), loadEntries()]);
});
</script>
